import useSWR, { mutate } from 'swr';
import { useMemo } from 'react';
import { fetcher, fetcherPost, fetcherPut, fetcherDelete, fetcherUpload } from 'utils/axios';
import { handleApiError, showSuccess } from 'utils/errorHandler';
import { CrudEndpoints } from 'types/common';

// Helper function to update list cache by adding/updating an item
export function updateListCache(fetchedItem: any, resourceKey: string, idFieldName: string = 'primaryKeyID') {
    // Extract single item from array if response is an array
    let newItem = Array.isArray(fetchedItem) ? fetchedItem[0] : fetchedItem;

    // If it's a wrapped response (e.g., { summary: ..., projects: [...] }), extract the first item from the array
    if (newItem && typeof newItem === 'object' && !Array.isArray(newItem)) {
        const arrayProp = Object.keys(newItem).find((k) => {
            const arr = newItem[k];
            return Array.isArray(arr) && arr.length > 0 && (arr[0]?.[idFieldName] !== undefined || arr[0]?.primaryKeyID !== undefined);
        });
        if (arrayProp) {
            newItem = newItem[arrayProp][0];
        }
    }

    if (!newItem) return;

    // Mutate all keys that start with the resourceKey (handles filtered lists and array keys)
    mutate(
        (key) => {
            if (typeof key === 'string') return key.startsWith(resourceKey);
            if (Array.isArray(key) && typeof key[0] === 'string') return key[0].startsWith(resourceKey);
            return false;
        },
        (currentData: any) => {
            if (!currentData) return currentData;

            // Heuristic to find the array/list in the data structure
            let items: any[] = [];
            let propName = '';

            if (Array.isArray(currentData)) {
                items = currentData;
            } else {
                // Common paths: tenants, data, users, etc.
                propName = Object.keys(currentData).find((k) => Array.isArray(currentData[k])) || '';
                if (propName) {
                    items = currentData[propName];
                } else {
                    return currentData;
                }
            }

            // Heuristic to find the correct ID field (e.g., tenantID, cityStateID, primaryKeyID, defaultFileCategoryID)
            const getID = (obj: any) => obj?.[idFieldName] || obj?.primaryKeyID;
            const newItemId = getID(newItem);

            if (newItemId === undefined) return currentData;

            const existingIndex = items.findIndex((item: any) => getID(item) === newItemId);

            let updatedItems;
            if (existingIndex > -1) {
                updatedItems = [...items];
                updatedItems[existingIndex] = newItem;
            } else {
                updatedItems = [...items, newItem];
            }

            return propName ? { ...currentData, [propName]: updatedItems } : updatedItems;
        },
        { revalidate: false }
    );
}

// Helper function to remove item from list cache
export function removeFromListCache(itemId: number | string, resourceKey: string, idFieldName: string = 'primaryKeyID') {
    // Mutate all keys that start with the resourceKey (handles filtered lists and array keys)
    mutate(
        (key) => {
            if (typeof key === 'string') return key.startsWith(resourceKey);
            if (Array.isArray(key) && typeof key[0] === 'string') return key[0].startsWith(resourceKey);
            return false;
        },
        (currentData: any) => {
            if (!currentData) return currentData;

            let items: any[] = [];
            let propName = '';

            if (Array.isArray(currentData)) {
                items = currentData;
            } else {
                propName = Object.keys(currentData).find((k) => Array.isArray(currentData[k])) || '';
                if (propName) {
                    items = currentData[propName];
                } else {
                    return currentData;
                }
            }

            const getID = (obj: any) => obj?.[idFieldName] || obj?.primaryKeyID;

            const filteredItems = items.filter((item: any) => getID(item) !== itemId);

            return propName ? { ...currentData, [propName]: filteredItems } : filteredItems;
        },
        { revalidate: false }
    );
}

export function createGenericCRUD<T extends Record<string, any>>(endpoints: CrudEndpoints) {
    // Get list - called once on mount
    function useGetList(showAll?: boolean) {
        const listEndpoint = `${endpoints.key}${endpoints.getList}`;
        const requestKey = showAll === undefined ? listEndpoint : `${listEndpoint}?showAll=${showAll}`;
        const {
            data: listData,
            isLoading,
            error,
            isValidating,
            mutate: boundMutate
        } = useSWR(requestKey, fetcher, {
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            onError: handleApiError
        });

        return useMemo(
            () => ({
                data: listData?.records || listData?.data || listData || [],
                isLoading,
                error,
                isValidating,
                mutate: boundMutate,
                totalRecordCount: listData?.totalCount ?? listData?.totalRecordCount ?? listData?.length,
                isEmpty: !isLoading && !(listData?.records || listData?.data || listData)?.length
            }),
            [listData, error, isLoading, isValidating, boundMutate]
        );
    }

    // Get list with ID filter
    function useGetListByID(id?: number | string | null) {
        const fullEndpoint = id ? `${endpoints.key}${endpoints.getListById.replace('{ID}', id.toString())}` : null;

        const {
            data: itemData,
            isLoading,
            error,
            isValidating,
            mutate: boundMutate
        } = useSWR(fullEndpoint, fetcher, {
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            onError: handleApiError
        });

        return useMemo(
            () => ({
                data: itemData || null,
                isLoading,
                error,
                isValidating,
                mutate: boundMutate,
                isEmpty: !isLoading && !itemData
            }),
            [itemData, error, isLoading, isValidating, boundMutate]
        );
    }

    // Get modal data
    function useGetModal(id?: number | string | null) {
        const endpoint = id ? `${endpoints.key}${endpoints.getModal}/${id}` : null;

        const {
            data: modalData,
            isLoading,
            error,
            isValidating,
            mutate: boundMutate
        } = useSWR(endpoint, fetcher, {
            revalidateIfStale: true,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            onError: handleApiError
        });

        return useMemo(
            () => ({
                data: modalData || null,
                isLoading,
                error,
                isValidating,
                mutate: boundMutate,
                isEmpty: !isLoading && !modalData
            }),
            [modalData, error, isLoading, isValidating, boundMutate]
        );
    }

    // Get lookup list
    function useGetLookupList() {
        const lookupEndpoint = `${endpoints.key}${endpoints.getLookupList}`;

        const swrKey = useMemo(() => {
            return endpoints.getlookupPost
                ? [lookupEndpoint, { data: { includeIDs: [] as Array<number | string> } }]
                : lookupEndpoint;
        }, [lookupEndpoint]);

        const {
            data: lookupData,
            isLoading,
            error,
            isValidating,
            mutate: boundMutate
        } = useSWR(swrKey, endpoints.getlookupPost ? fetcherPost : fetcher, {
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            onError: handleApiError
        });

        return useMemo(
            () => ({
                data: lookupData?.data || lookupData || [],
                isLoading,
                error,
                isValidating,
                mutate: boundMutate,
                isEmpty: !isLoading && !(lookupData?.data || lookupData)?.length
            }),
            [lookupData, error, isLoading, isValidating, boundMutate]
        );
    }

    // Helper function to build URI with query parameters
    function buildUri(base: string, params?: Record<string, any>) {
        if (!params || Object.keys(params).length === 0) return base;
        const query = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                query.append(key, value.toString());
            }
        });
        const queryString = query.toString();
        return queryString ? `${base}${base.includes('?') ? '&' : '?'}${queryString}` : base;
    }

    // Insert operation
    async function useInsertRecord(
        newItem: Partial<T>,
        listParams?: Record<string, any>,
        options?: { disableCacheUpdate?: boolean; invalidateKey?: string }
    ) {
        try {
            const result = await fetcherPost([`${endpoints.key}${endpoints.insert}`, { data: newItem }]);
            if (result?.primaryKeyID) {
                const itemId = result?.primaryKeyID;
                if (options?.disableCacheUpdate) {
                    if (options?.invalidateKey) {
                        invalidateResourceCache(options.invalidateKey);
                    } else {
                        invalidateResourceCache(`${endpoints.key}${endpoints.getList}`);
                    }
                    showSuccess('Created successfully');
                    return result;
                }

                // Fetch the new item using GetList/{ID} or fallback to the result
                const listKey = buildUri(`${endpoints.key}${endpoints.getList}`, listParams);
                let fetchedItem = null;
                if (endpoints.getListById && !endpoints.getListPost) {
                    const getByIdEndpoint = endpoints.getListById.replace('{ID}', itemId.toString());
                    const fullEndpoint = buildUri(`${endpoints.key}${getByIdEndpoint}`, listParams);
                    fetchedItem = await fetcher(fullEndpoint).catch(() => null);
                } else if (endpoints.getListPost) {
                    // Fetch via POST to GetList with ID in body
                    const fullEndpoint = `${endpoints.key}${endpoints.getList}`;
                    const idField = endpoints.idFieldName || 'primaryKeyID';
                    const body = { ...(endpoints.listPostParams || {}), ...listParams, [idField]: itemId };
                    fetchedItem = await fetcherPost([fullEndpoint, { data: body }]).catch(() => null);
                }

                let finalItem = fetchedItem || result;
                if (fetchedItem) {
                    let extracted = Array.isArray(fetchedItem) ? fetchedItem[0] : fetchedItem;
                    if (extracted && typeof extracted === 'object' && !Array.isArray(extracted)) {
                        const arrayProp = Object.keys(extracted).find((k) => {
                            const arr = extracted[k];
                            return Array.isArray(arr) && arr.length > 0 && (arr[0]?.[endpoints.idFieldName || 'primaryKeyID'] !== undefined || arr[0]?.primaryKeyID !== undefined);
                        });
                        if (arrayProp) {
                            extracted = extracted[arrayProp][0];
                        }
                    }
                    finalItem = extracted || finalItem;
                }

                // Update the list cache with the new item or the result from insert
                updateListCache(finalItem, listKey, endpoints.idFieldName);
                showSuccess('Created successfully');
                return finalItem;
            }
            return null;
        } catch (error) {
            handleApiError(error);
            return null;
        }
    }

    // Update operation
    async function useUpdateRecord(
        itemId: number | string,
        updatedItem: Partial<T>,
        listParams?: Record<string, any>,
        options?: { disableCacheUpdate?: boolean; invalidateKey?: string, showSnackbar?: boolean }

    ) {
        try {
            const showSnakebar = options?.showSnackbar ?? true;
            const result = await fetcherPut([`${endpoints.key}${endpoints.update}`, { data: updatedItem }]);
            if (result?.primaryKeyID) {
                const id = result?.primaryKeyID;
                if (options?.disableCacheUpdate) {
                    if (options?.invalidateKey) {
                        invalidateResourceCache(options.invalidateKey);
                    } else {
                        invalidateResourceCache(`${endpoints.key}${endpoints.getList}`);
                    }
                    showSnakebar !== false && showSuccess('Updated successfully');
                    return result;
                }

                // Fetch the updated item using GetList/{ID} or fallback to the result
                const listKey = buildUri(`${endpoints.key}${endpoints.getList}`, listParams);
                let fetchedItem = null;
                if (endpoints.getListById && !endpoints.getListPost) {
                    const getByIdEndpoint = endpoints.getListById.replace('{ID}', id.toString());
                    const fullEndpoint = buildUri(`${endpoints.key}${getByIdEndpoint}`, listParams);
                    fetchedItem = await fetcher(fullEndpoint).catch(() => null);
                } else if (endpoints.getListPost) {
                    // Fetch via POST to GetList with ID in body
                    const fullEndpoint = `${endpoints.key}${endpoints.getList}`;
                    const idField = endpoints.idFieldName || 'primaryKeyID';
                    const body = { ...(endpoints.listPostParams || {}), ...listParams, [idField]: id };
                    fetchedItem = await fetcherPost([fullEndpoint, { data: body }]).catch(() => null);
                }

                let finalItem = fetchedItem || result;
                if (fetchedItem) {
                    let extracted = Array.isArray(fetchedItem) ? fetchedItem[0] : fetchedItem;
                    if (extracted && typeof extracted === 'object' && !Array.isArray(extracted)) {
                        const arrayProp = Object.keys(extracted).find((k) => {
                            const arr = extracted[k];
                            return Array.isArray(arr) && arr.length > 0 && (arr[0]?.[endpoints.idFieldName || 'primaryKeyID'] !== undefined || arr[0]?.primaryKeyID !== undefined);
                        });
                        if (arrayProp) {
                            extracted = extracted[arrayProp][0];
                        }
                    }
                    finalItem = extracted || finalItem;
                }

                // Update the list cache with the updated item or the result from update
                updateListCache(finalItem, listKey, endpoints.idFieldName);
                showSnakebar !== false && showSuccess('Updated successfully');
                return finalItem;
            }
            return null;
        } catch (error) {
            handleApiError(error);
            return null;
        }
    }

    // Delete operation
    async function useDeleteRecord(itemId: number | string) {
        try {
            const result = await fetcherDelete([`${endpoints.key}${endpoints.delete}/${itemId}`, {}]);
            // Remove the item from the list cache instead of refetching the entire list
            const listKey = `${endpoints.key}${endpoints.getList}`;
            removeFromListCache(itemId, listKey, endpoints.idFieldName);
            showSuccess('Deleted successfully');
            return result;
        } catch (error) {
            handleApiError(error);
            return null;
        }
    }

    return {
        useGetList,
        useGetListByID,
        useGetModal,
        useGetLookupList,
        useInsertRecord,
        useUpdateRecord,
        useDeleteRecord
    };
}

export async function useUploadFileData<T = any>(
    endpoint: string,
    file: File | null,
    extraData?: Record<string, any>,
    options?: { showSnackbar?: boolean; method?: string; headers?: Record<string, string>; timeout?: number }
): Promise<T | null> {
    if (!endpoint) {
        return null;
    }
    try {
        const result = await fetcherUpload([
            endpoint,
            {
                file,
                data: extraData,
                method: options?.method,
                headers: options?.headers,
                timeout: options?.timeout ?? 0
            }
        ]);

        if (options?.showSnackbar !== false) {
            showSuccess(extraData?.message || '');
        }
        return result as T;
    } catch (error) {
        handleApiError(error);
        return null;
    }
}

export async function invalidateCache(endpoint: string, clearData: boolean = false) {
    if (clearData) {
        await mutate(endpoint, undefined, { revalidate: true });
    } else {
        await mutate(endpoint);
    }
}

// Helper: Invalidate all cache keys for this resource
export function invalidateResourceCache(key: string) {
    mutate((cacheKey) => {
        if (typeof cacheKey === 'string') {
            return cacheKey.startsWith(key);
        } else if (Array.isArray(cacheKey) && cacheKey.length > 0 && typeof cacheKey[0] === 'string') {
            return cacheKey[0].startsWith(key);
        }
        return false;
    });
}

